// ProfileDialog — self-service edição de perfil (nome, email, telemóvel, função)
// Qualquer utilizador autenticado pode editar os seus próprios dados.
// A password também pode ser alterada (com verificação da password actual).
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User as UserIcon, Save, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", job_function: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  useEffect(() => {
    if (open && user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        job_function: user.job_function || "",
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
    }
  }, [open, user]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("O nome não pode ficar vazio");
      return;
    }
    // Se preencheu new_password, garante que confirm bate certo
    const changingPw = !!pw.new_password;
    if (changingPw) {
      if (pw.new_password.length < 6) {
        toast.error("Nova password precisa de pelo menos 6 caracteres");
        return;
      }
      if (pw.new_password !== pw.confirm) {
        toast.error("A confirmação da nova password não bate certo");
        return;
      }
      if (!pw.current_password) {
        toast.error("Introduza a password actual para poder alterar");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (changingPw) {
        payload.current_password = pw.current_password;
        payload.new_password = pw.new_password;
      }
      await axios.put(`${API}/auth/me`, payload);
      await refreshUser();
      toast.success("Perfil atualizado com sucesso");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-brand-600" />
            O Meu Perfil
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Estes dados aparecem no slide &ldquo;Obrigado!&rdquo; do PDF de propostas que gerares.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome próprio + apelido"
              className="mt-1"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="teu.email@evolutt.io"
              className="mt-1"
              data-testid="profile-email-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Telemóvel</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+351 912 345 678"
                className="mt-1"
                data-testid="profile-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs">Função / Cargo</Label>
              <Input
                value={form.job_function}
                onChange={(e) => setForm({ ...form, job_function: e.target.value })}
                placeholder="Ex: Talent Acquisition"
                className="mt-1"
                data-testid="profile-job-function-input"
              />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 mt-2">
            <div className="flex items-center gap-2 mb-2 text-slate-700">
              <KeyRound className="w-4 h-4" />
              <span className="text-sm font-medium">Alterar Password (opcional)</span>
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Password actual"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                data-testid="profile-current-password-input"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="password"
                  placeholder="Nova password (≥6 caracteres)"
                  value={pw.new_password}
                  onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                  data-testid="profile-new-password-input"
                />
                <Input
                  type="password"
                  placeholder="Confirmar nova password"
                  value={pw.confirm}
                  onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                  data-testid="profile-confirm-password-input"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="profile-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90"
            data-testid="profile-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
